import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { applyAccent } from "@/lib/theme";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("finops_user") || "null"); } catch { return null; }
  });
  const [org, setOrg] = useState(() => {
    try { return JSON.parse(localStorage.getItem("finops_org") || "null"); } catch { return null; }
  });
  const [lang, setLang] = useState(() => localStorage.getItem("finops_lang") || "en");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Auth is cookie-based (httpOnly `zenith_auth` + `zenith_csrf`) — /auth/me
    // will succeed if the cookie is valid, else 401 → the axios interceptor
    // redirects to /login.
    api.get("/auth/me").then((r) => {
      setUser(r.data.user); setOrg(r.data.org);
      localStorage.setItem("finops_user", JSON.stringify(r.data.user));
      localStorage.setItem("finops_org", JSON.stringify(r.data.org));
      // Iter-193 · Push the tenant-picked accent to CSS on hydrate
      const hx = r.data.org?.appearance?.accent_hex;
      if (hx) applyAccent(hx);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const login = async (email, password, org_id) => {
    const payload = { email, password };
    if (org_id) payload.org_id = org_id;
    const r = await api.post("/auth/login", payload);
    localStorage.setItem("finops_user", JSON.stringify(r.data.user));
    localStorage.setItem("finops_org", JSON.stringify(r.data.org));
    setUser(r.data.user); setOrg(r.data.org);
    // Iter-193 · Apply tenant accent on login too
    const hx = r.data.org?.appearance?.accent_hex;
    if (hx) applyAccent(hx);
    return r.data;   // {token, user, org, orgs?, needs_org_select?}
  };

  const switchOrg = async (orgId) => {
    const r = await api.post(`/auth/switch-org/${orgId}`);
    // Wipe stale org/user from localStorage FIRST — otherwise a fast component
    // mount (e.g. Sidebar/Topbar reading `finops_org` on init) briefly renders
    // the old company name before /auth/me hydrates.
    try {
      localStorage.removeItem("finops_user");
      localStorage.removeItem("finops_org");
    } catch { /* noop */ }
    const me = await api.get("/auth/me");
    localStorage.setItem("finops_user", JSON.stringify(me.data.user));
    localStorage.setItem("finops_org", JSON.stringify(me.data.org));
    setUser(me.data.user); setOrg(me.data.org);
    return r.data;
  };

  const signup = async (payload) => {
    const r = await api.post("/auth/signup", payload);
    localStorage.setItem("finops_user", JSON.stringify(r.data.user));
    localStorage.setItem("finops_org", JSON.stringify(r.data.org));
    setUser(r.data.user); setOrg(r.data.org);
    return r.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* server-side cookie clear best-effort */ }
    localStorage.removeItem("finops_user");
    localStorage.removeItem("finops_org");
    setUser(null); setOrg(null);
    window.location.href = "/login";
  };

  const setLanguage = (l) => { setLang(l); localStorage.setItem("finops_lang", l); };

  // Iter-145 · Retro-toggle callers (Settings → AddonModulesSection) need to
  // mutate the org locally so the sidebar re-renders new modules instantly.
  const patchOrgLocal = (partial) => {
    setOrg((cur) => {
      const next = { ...(cur || {}), ...(partial || {}) };
      try { localStorage.setItem("finops_org", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  return (
    <AuthCtx.Provider value={{ user, org, loading, login, signup, logout, switchOrg, lang, setLanguage, patchOrgLocal }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
